import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';

import { useAvo } from '@hooks/Avo/useAvo';

import { MenuLabel, NavigationComponent, NavigationComponentValueType } from 'src/context/Avo/generated-avo';
import { NavigationURLs } from 'src/lib/navigation/types';

const getMenuLabel = (route: NavigationURLs) => {
  switch (route) {
    case NavigationURLs.UniversalTrade:
      return MenuLabel.TRADE;
    case NavigationURLs.AviaryAssetsAll:
      return MenuLabel.ASSETS;
    case NavigationURLs.Bundles:
      return MenuLabel.BUNDLES;
    case NavigationURLs.AutoInvest:
      return MenuLabel.AUTO_INVEST;
    case NavigationURLs.TransactionsRoot:
      return MenuLabel.TRANSACTIONS;
    case NavigationURLs.WithdrawSend:
    case NavigationURLs.DepositReceive:
      return MenuLabel.DEPOSIT_WITHDRAW;
    case NavigationURLs.WalletsRoot:
      return MenuLabel.WALLETS;
    case NavigationURLs.Dashboard:
      return MenuLabel.DASHBOARD;
    case NavigationURLs.RewardsRoot:
      return MenuLabel.REWARDS;
    case NavigationURLs.Loans:
      return MenuLabel.LOANS;
    case NavigationURLs.QuickBuy:
      return MenuLabel.QUICK_BUY;
    default:
      return null;
  }
};

type AvoTracking = ReturnType<typeof useAvo>;

const routeEventHandlers: Partial<Record<NavigationURLs, (avo: AvoTracking, screen: string, layout: string) => void>> =
  {
    [NavigationURLs.AutoInvest]: (avo, screen, layout) => {
      avo.recurringOrderOpened({
        screen,
        layout,
        source: 'sidebar',
      });
    },
    [NavigationURLs.WithdrawSend]: (avo, screen, layout) => {
      avo.withdrawSendNavClicked({
        screen,
        layout,
      });
    },
    [NavigationURLs.DepositReceive]: (avo, screen) => {
      avo.depositFundsTapped({
        screen,
        component: '',
      });
    },
  };

const useAppSideBarAvoEvents = () => {
  const { pathname } = useLocation();
  const avo = useAvo();
  const isXs = useTailwindBreakpoint('xs');

  const trackRouteNavigation = useCallback(
    (route: NavigationURLs) => {
      if (!route) return;

      const menuLabelValue = getMenuLabel(route);
      const layout = isXs ? 'mobile' : 'desktop';

      if (menuLabelValue) {
        const navigationComponent: NavigationComponentValueType = isXs
          ? NavigationComponent.WEB_HEADER
          : NavigationComponent.WEB_SIDE_MENU;

        avo.navigationItemClicked({
          screen: pathname,
          menuLabel: menuLabelValue,
          navigationComponent,
        });
      } else {
        // Fallback for untracked routes
        avo.untrackedItemClicked({
          screen: pathname,
          layout,
        });
      }

      // route-specific event if handler exists
      const routeHandler = routeEventHandlers[route];
      if (routeHandler) {
        routeHandler(avo, pathname, layout);
      }
    },
    [avo, isXs, pathname],
  );

  return { trackRouteNavigation };
};

export { useAppSideBarAvoEvents };
